import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useRef, useState, useContext, useCallback, useEffect, useMemo } from "react";
import { Color } from "../common/enums/colors.js";
import '../common/components/ticker-bar/ticker-bar.css';const TickerBarContext = createContext({ barElement: null });
function TickerBarItem({
  content,
  refMap,
  id,
  textColor
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: `ticker-bar__item ticker-bar__item__${textColor ?? "regular"}`,
      ref: (node) => {
        if (node) {
          refMap.set(id, node);
        } else {
          refMap.delete(id);
        }
      },
      children: /* @__PURE__ */ jsx("div", { className: "ticker-bar__item__wrapper", children: content })
    }
  );
}
function TickerBarSection({
  title,
  items,
  refMap,
  id,
  speed = 0.5,
  onFinish = () => {
  },
  active = false,
  color = Color.DARK_GREY,
  textColor,
  isSingleSection,
  onRender = (itemCount, sectionID) => {
    return { itemCount, sectionID };
  }
}) {
  const titleRef = useRef(null);
  const itemsRef = useRef(/* @__PURE__ */ new Map());
  const [animating, setAnimating] = useState(false);
  const activeRef = useRef(null);
  const speedRef = useRef(speed ?? 1);
  const [itemCount, setItemCount] = useState(items.length);
  const visibleItems = useRef(items);
  const animationRef = useRef(null);
  const animatingRef = useRef(false);
  const { barElement } = useContext(TickerBarContext);
  const getTickerBar = useCallback(
    (element) => {
      if (barElement) return barElement;
      if (element === null || element === void 0) {
        return null;
      }
      if (element.classList.contains("ticker-bar")) {
        return element;
      }
      return getTickerBar(element.parentElement);
    },
    [barElement]
  );
  const setInitialElementOffset = useCallback(
    (elements) => {
      const firstElement = elements[0];
      if (!firstElement) {
        return;
      }
      const tickerBar = getTickerBar(firstElement);
      const initialWidth = ((tickerBar == null ? void 0 : tickerBar.clientWidth) ?? window.innerWidth) + (firstElement.clientWidth || 100) * Math.min(3, speedRef.current);
      const staticOffset = 10;
      let leftOffset = staticOffset;
      elements.forEach((element, index) => {
        const { style } = element;
        if (index === 0) {
          style.left = `${initialWidth - element.offsetWidth}px`;
          leftOffset += initialWidth;
        } else {
          const width = element.offsetWidth + staticOffset;
          style.left = `${leftOffset}px`;
          leftOffset += width;
        }
      });
    },
    [getTickerBar]
  );
  const marqueeHasItems = useCallback((elements) => {
    const firstElement = elements[0];
    return firstElement !== null && firstElement !== void 0;
  }, []);
  const handleRender = useCallback(() => {
    return onRender(itemCount, id);
  }, [id, onRender, itemCount]);
  const restartMarquee = useCallback(() => {
    const marqueeElements = [...itemsRef.current.values()];
    marqueeHasItems(marqueeElements) || true;
    {
      setInitialElementOffset(marqueeElements);
      setAnimating(true);
    }
  }, [setInitialElementOffset, marqueeHasItems]);
  const finish = useCallback(() => {
    let timeoutID = null;
    setAnimating(false);
    onFinish(id);
    animatingRef.current = false;
    visibleItems.current = [...items];
    setItemCount(items.length);
    handleRender();
    if (isSingleSection) {
      timeoutID = setTimeout(() => {
        restartMarquee();
      }, 450);
    }
    return () => {
      if (timeoutID) clearTimeout(timeoutID);
    };
  }, [handleRender, id, isSingleSection, items, onFinish, restartMarquee]);
  const moveItemForward = useCallback(
    (element, index, totalElements) => {
      const currentLeft = parseFloat(getComputedStyle(element).left);
      const newLeft = currentLeft - speedRef.current;
      const { style } = element;
      style.left = `${newLeft}px`;
      if (newLeft + element.offsetWidth < (titleRef.current ? titleRef.current.clientWidth : 15) && index === totalElements - 1) {
        finish();
      }
    },
    [finish]
  );
  const animateReset = useCallback(() => {
    const thisElement = refMap.get(id);
    if (thisElement) {
      setTimeout(() => {
        thisElement.style.opacity = "0";
      }, 250);
      setTimeout(() => {
        thisElement.style.transform = ``;
        setTimeout(() => {
          thisElement.style.opacity = "";
        }, 250);
      }, 500);
    }
  }, [id, refMap]);
  const moveMarqueeForward = useCallback(
    (elements) => {
      if (active && animating && animatingRef.current) {
        if (elements.length === 0) {
          finish();
        } else {
          elements.forEach((element, index) => {
            moveItemForward(element, index, elements.length);
          });
        }
      }
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(() => {
        moveMarqueeForward(elements);
      });
    },
    [active, animating, moveItemForward, finish]
  );
  useEffect(() => {
    if (speedRef.current !== speed) {
      speedRef.current = speed;
    }
  }, [speed]);
  useEffect(() => {
    if (animating && !animatingRef.current) {
      const marqueeElements = [...itemsRef.current.values()];
      moveMarqueeForward(marqueeElements);
      animatingRef.current = true;
    } else if (!animating && animatingRef.current) {
      animatingRef.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, [animating, moveMarqueeForward, speed]);
  useEffect(() => {
    if (visibleItems.current.length === 0 && items.length > 0) {
      visibleItems.current = items;
      setItemCount(items.length);
      handleRender();
    }
  }, [items, handleRender]);
  useEffect(() => {
    let timeoutID = null;
    if (itemCount === 0 && active) {
      timeoutID = setTimeout(() => {
        finish();
      }, 100);
    } else if (itemCount > 0 && active && !animating) {
      restartMarquee();
    }
    return () => {
      if (timeoutID) clearTimeout(timeoutID);
    };
  }, [itemCount, active, finish, animating, restartMarquee]);
  useEffect(() => {
    if (active) {
      restartMarquee();
    }
    if (activeRef.current !== active) {
      if (active && !activeRef.current && activeRef.current !== null) {
        setTimeout(
          () => {
            restartMarquee();
          },
          (speed > 3 ? 2 : speed) * 125
        );
      } else if (!active && activeRef.current !== null) {
        animateReset();
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      }
      activeRef.current = active;
    }
  }, [active, animateReset, restartMarquee, setInitialElementOffset, speed]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `ticker-bar__section ${active ? "active" : "inactive"} ${itemCount > 0 ? "ticker-bar__full__section" : "ticker-bar__empty__section"}`,
      ref: (node) => {
        if (node) {
          refMap.set(id, node);
        } else {
          refMap.delete(id);
        }
      },
      children: [
        /* @__PURE__ */ jsx("div", { className: `ticker-bar__section__title ${color}`, ref: titleRef, children: /* @__PURE__ */ jsx("span", { children: title }) }),
        /* @__PURE__ */ jsx("div", { className: "ticker-bar__section__items", children: visibleItems.current.map((item) => /* @__PURE__ */ jsx(
          TickerBarItem,
          {
            textColor: item.textColor || textColor,
            content: item.content,
            id: item.id,
            refMap: itemsRef.current
          },
          item.id
        )) })
      ]
    }
  );
}
function TickerBar({
  sections = [],
  speed = 2,
  onSectionChange = () => {
  },
  color = Color.DARK_GREY,
  textColor = Color.MEDIUM_GREY
}) {
  let initialSectionID;
  if (sections && sections.length) {
    initialSectionID = sections[0].id;
  }
  const [allowSectionChange, setAllowSectionChange] = useState(true);
  const [currentBar, setCurrentBar] = useState(null);
  const sectionsItemsRef = useRef(/* @__PURE__ */ new Map());
  const sectionsRef = useRef(/* @__PURE__ */ new Map());
  const barRef = useRef(null);
  const [activeSection, setActiveSection] = useState(initialSectionID);
  const currentBarContext = useMemo(() => {
    return { barElement: currentBar };
  }, [currentBar]);
  const getNextSection = useCallback(
    (sectionIndex) => {
      let nextSection;
      if (sectionIndex + 1 !== sections.length) {
        nextSection = sections[sectionIndex + 1];
      }
      if (!nextSection) {
        const [firstSection] = sections;
        nextSection = firstSection;
      }
      return nextSection;
    },
    [sections]
  );
  const setNextSection = useCallback(
    (nextSection) => {
      if (allowSectionChange) setActiveSection(nextSection.id);
    },
    [allowSectionChange]
  );
  const handleFinish = useCallback(
    (id) => {
      if (id !== activeSection) {
        return;
      }
      if (sections.length === 1) {
        setActiveSection(sections[0].id);
        return;
      }
      const sectionIDs = sections.map((section) => section.id);
      const sectionIndex = sectionIDs.indexOf(id);
      const sectionElement = sectionsRef.current.get(id);
      const nextSection = getNextSection(sectionIndex);
      if (nextSection) {
        if (nextSection.items.length === 0) {
          setActiveSection(sections[0].id);
          return;
        }
        if (sectionElement) {
          sectionElement.style.transform = `translateY(44px)`;
        }
        setNextSection(nextSection);
      }
    },
    [activeSection, getNextSection, sections, setNextSection]
  );
  const handleOnRender = useCallback(
    (itemCount, sectionID) => {
      const { current } = sectionsItemsRef;
      current.set(sectionID, itemCount);
      sectionsItemsRef.current = current;
      if ([...current.keys()].length === sections.length) {
        const hasAnyItems = [...current.values()].filter((value) => value > 0).length > 0;
        setAllowSectionChange(hasAnyItems);
      }
    },
    [sections]
  );
  useEffect(() => {
    sections.forEach((section) => {
      const { current } = sectionsItemsRef;
      if (!current.has(section.id) || !current.get(section.id)) {
        current.set(section.id, section.items.length);
        sectionsItemsRef.current = current;
      }
    });
  }, [sections]);
  useEffect(() => {
    let timeout;
    const checkSection = () => {
      const section = sections.find((s) => s.id === activeSection);
      if (section) {
        onSectionChange(section);
        const { current } = sectionsItemsRef;
        if (!sectionsItemsRef.current.has(section.id)) {
          current.set(section.id, section.items.length);
          sectionsItemsRef.current = current;
        }
        if ([...current.keys()].length === sections.length) {
          const hasAnyItems = [...current.values()].filter((value) => value > 0).length > 0;
          setAllowSectionChange(hasAnyItems);
        }
        return true;
      }
      return false;
    };
    const checkResult = () => {
      const result = checkSection();
      if (!result) {
        timeout = setTimeout(() => {
          checkResult();
        }, 1e3);
      } else if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
    checkResult();
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [activeSection, onSectionChange, sections]);
  const setBarRef = (div) => {
    if (!barRef.current) {
      barRef.current = div;
      setCurrentBar(barRef.current);
    }
  };
  return /* @__PURE__ */ jsx("div", { className: "ticker-bar themed-ticker-bar", ref: setBarRef, children: /* @__PURE__ */ jsx(TickerBarContext.Provider, { value: currentBarContext, children: (sections ?? []).map((section) => /* @__PURE__ */ jsx(
    TickerBarSection,
    {
      onRender: handleOnRender,
      isSingleSection: sections.length === 1,
      color: section.color || color,
      textColor: section.textColor || textColor,
      speed: section.speed || speed,
      onFinish: handleFinish,
      active: activeSection === section.id,
      items: section.items,
      title: section.title,
      refMap: sectionsRef.current,
      id: section.id
    },
    section.id
  )) }) });
}
export {
  TickerBar
};
//# sourceMappingURL=ticker-bar.js.map
